import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';

const ISO = 'YYYY-MM-DD';
const fmt = (d: Dayjs) => d.format('D MMM YYYY');

type RangeDayProps = PickersDayProps<Dayjs> & { rangeStart?: Dayjs | null; rangeEnd?: Dayjs | null };

const DayCell = styled(PickersDay as React.ComponentType<PickersDayProps<Dayjs>>, {
  shouldForwardProp: (p) => p !== 'inRange' && p !== 'isEdge',
})<{ inRange?: boolean; isEdge?: boolean }>(({ theme, inRange, isEdge }) => ({
  ...(inRange && {
    borderRadius: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.text.primary,
    '&:hover, &:focus': { backgroundColor: alpha(theme.palette.primary.main, 0.3) },
  }),
  ...(isEdge && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark },
  }),
}));

/** Calendar day that shades the selected start → end range. */
const RangeDay: React.FC<RangeDayProps> = ({ rangeStart, rangeEnd, day, ...other }) => {
  const isStart = !!rangeStart && day.isSame(rangeStart, 'day');
  const isEnd = !!rangeEnd && day.isSame(rangeEnd, 'day');
  const inRange =
    !!rangeStart && !!rangeEnd && day.isAfter(rangeStart, 'day') && day.isBefore(rangeEnd, 'day');
  return (
    <DayCell
      {...other}
      day={day}
      selected={false}
      disableMargin
      inRange={inRange && !other.outsideCurrentMonth}
      isEdge={(isStart || isEnd) && !other.outsideCurrentMonth}
    />
  );
};

/** Earliest and latest dates that have USAJILI data, so presets anchor to real data. */
function useDataBounds() {
  const { refetchKey } = useUSAJILI();
  return useQuery({
    queryKey: ['usajili_date_bounds', refetchKey],
    queryFn: async () => {
      const [first, last] = await Promise.all([
        usajiliApi.dailyList({ ordering: 'date', page_size: 1 }),
        usajiliApi.dailyList({ ordering: '-date', page_size: 1 }),
      ]);
      return {
        min: first.results[0]?.date ? dayjs(first.results[0].date) : null,
        max: last.results[0]?.date ? dayjs(last.results[0].date) : null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Date-range filter: one button that opens quick presets plus a calendar.
 * Click a start day, then an end day, then Apply.
 */
export const USAJILIDateRangePicker: React.FC = () => {
  const { filters, setFilters } = useUSAJILI();
  const { data: bounds } = useDataBounds();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);

  const appliedFrom = filters.date_from ? dayjs(filters.date_from) : null;
  const appliedTo = filters.date_to ? dayjs(filters.date_to) : null;

  const label = useMemo(() => {
    if (!appliedFrom && !appliedTo) return 'All dates';
    if (appliedFrom && appliedTo && appliedFrom.isSame(appliedTo, 'day')) return fmt(appliedFrom);
    return `${appliedFrom ? fmt(appliedFrom) : 'Start'} – ${appliedTo ? fmt(appliedTo) : 'Latest'}`;
  }, [appliedFrom, appliedTo]);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setStart(appliedFrom);
    setEnd(appliedTo);
    setAnchor(e.currentTarget);
  };

  const apply = (from: Dayjs | null, to: Dayjs | null) => {
    setFilters((f) => ({
      ...f,
      date_from: from ? from.format(ISO) : '',
      date_to: to ? to.format(ISO) : from ? from.format(ISO) : '',
    }));
    setAnchor(null);
  };

  const onPick = (d: Dayjs | null) => {
    if (!d) return;
    if (!start || end) {
      // first click (or restarting after a complete range)
      setStart(d);
      setEnd(null);
    } else if (d.isBefore(start, 'day')) {
      setEnd(start);
      setStart(d);
    } else {
      setEnd(d);
    }
  };

  const latest = bounds?.max ?? dayjs();
  const presets: Array<{ label: string; range: [Dayjs | null, Dayjs | null] }> = [
    { label: 'All dates', range: [null, null] },
    { label: 'Latest day', range: [latest, latest] },
    { label: 'Last 7 days', range: [latest.subtract(6, 'day'), latest] },
    { label: 'Last 14 days', range: [latest.subtract(13, 'day'), latest] },
    { label: 'Last 30 days', range: [latest.subtract(29, 'day'), latest] },
    { label: 'This month', range: [latest.startOf('month'), latest] },
    {
      label: 'Last month',
      range: [latest.subtract(1, 'month').startOf('month'), latest.subtract(1, 'month').endOf('month')],
    },
  ];

  const hint = !start ? 'Click a start date' : !end ? 'Now click an end date' : `${fmt(start)} – ${fmt(end)}`;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={open}
        startIcon={<CalendarMonthIcon sx={{ color: 'primary.main' }} />}
        endIcon={<ArrowDropDownIcon />}
        aria-label="Choose date range"
        sx={{
          height: 34,
          px: 1.25,
          fontSize: '0.875rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: 'text.primary',
          borderColor: 'divider',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {label}
      </Button>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, border: '1px solid', borderColor: 'divider' } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          <List dense sx={{ minWidth: 150, py: 1 }}>
            <Typography sx={{ px: 2, pb: 0.5, fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
              QUICK SELECT
            </Typography>
            {presets.map((p) => (
              <ListItemButton key={p.label} onClick={() => apply(p.range[0], p.range[1])} sx={{ py: 0.5 }}>
                <ListItemText primary={p.label} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </ListItemButton>
            ))}
          </List>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <DateCalendar
              value={end ?? start}
              onChange={onPick}
              referenceDate={start ?? latest}
              minDate={bounds?.min ?? undefined}
              maxDate={bounds?.max ?? undefined}
              views={['day']}
              slots={{ day: RangeDay as any }}
              slotProps={{ day: { rangeStart: start, rangeEnd: end } as any }}
              sx={{ height: 'auto', '& .MuiDayCalendar-monthContainer': { position: 'relative' } }}
            />
            <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flex: 1 }}>{hint}</Typography>
              <Button size="small" onClick={() => { setStart(null); setEnd(null); }} sx={{ minWidth: 0, px: 1.5 }}>
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!start}
                onClick={() => apply(start, end ?? start)}
                sx={{ px: 2 }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
